import React, { Suspense } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Spinner, Text, VStack } from "@chakra-ui/react";
import Sidepanel from "components/Sidepanel";
import { MF_ACCESS_REVIEWS } from "state/members/accessReviews";
import CampaignDetails from "./CampaignDetails";

const CampaignSidepanel: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Fetch campaign details without filters (just campaign info and stats)
  const campaignDetailsQuery = MF_ACCESS_REVIEWS.useGetCampaignDetails(
    campaignId ?? "",
    undefined,
    { enabled: !!campaignId },
  );
  const { data: initialData, isLoading } = campaignDetailsQuery;
  const error = (campaignDetailsQuery as { error?: unknown }).error;

  const handleClose = () => {
    // Preserve campaign list filters (not review_ prefixed ones) when closing sidepanel
    const params = new URLSearchParams();
    searchParams.forEach((value, key) => {
      // Keep all params that don't start with "review_"
      if (!key.startsWith("review_")) {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    navigate(`/members/access-reviews${queryString ? `?${queryString}` : ""}`);
  };

  return (
    <Sidepanel isOpen={!!campaignId} onClose={handleClose}>
      {/* Add Suspense boundary inside the sidepanel to isolate any
          data loading and prevent triggering parent Suspense boundaries */}
      <Suspense
        fallback={
          <Flex justify="center" py={8}>
            <Spinner size="lg" color="gray.500" />
          </Flex>
        }
      >
        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" color="gray.500" />
          </Flex>
        ) : error ? (
          <VStack spacing={2} py={8}>
            <Text fontSize="md" fontWeight="500" color="red.600">
              Error loading campaign
            </Text>
            <Text fontSize="sm" color="gray.500">
              Unable to load campaign details
            </Text>
          </VStack>
        ) : initialData ? (
          <CampaignDetails
            campaignId={campaignId ?? ""}
            initialData={initialData}
          />
        ) : null}
      </Suspense>
    </Sidepanel>
  );
};

export default CampaignSidepanel;
